import React from "react";
import { Camera, Loader2, Trash2, Plus } from "lucide-react";
import { photoSessionTheme as theme } from "./photoSessionTheme";

export interface PhotoSessionReviewLabels {
  removePhoto?: string;
  retake?: string;
  nextPhoto?: string;
  uploading?: string;
  uploaded?: string;
}

interface PhotoSessionReviewViewProps {
  previewUrl: string;
  status: "pending" | "uploading" | "done" | "failed";
  canEdit: boolean;
  onRemove: () => void;
  onRetake: () => void;
  onContinueCapture: () => void;
  isVideo?: boolean;
  labels?: PhotoSessionReviewLabels;
}

export function PhotoSessionReviewView({
  previewUrl,
  status,
  canEdit,
  onRemove,
  onRetake,
  onContinueCapture,
  isVideo = false,
  labels = {},
}: PhotoSessionReviewViewProps) {
  const isUploading = status === "uploading";

  return (
    <div
      className="absolute inset-0 z-[60] flex flex-col rounded-xl overflow-hidden"
      style={{ backgroundColor: "#000", border: `1px solid ${theme.border}` }}
    >
      <div className="relative flex-1 min-h-0">
        {isVideo ? (
          <video
            src={previewUrl}
            controls
            playsInline
            className="absolute inset-0 w-full h-full object-contain"
          />
        ) : (
          <img
            src={previewUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
            draggable={false}
          />
        )}
        {isUploading && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <Loader2
              className="h-10 w-10 animate-spin"
              style={{ color: theme.accent }}
            />
            <p className="text-sm font-medium" style={{ color: theme.text }}>
              {labels.uploading ?? "Uploading…"}
            </p>
          </div>
        )}
        {status === "done" && (
          <div
            className="absolute top-3 left-3 rounded-lg px-2.5 py-1 text-[11px] font-semibold"
            style={{ backgroundColor: "rgba(0,0,0,0.72)", color: theme.success }}
          >
            {labels.uploaded ?? "Uploaded"}
          </div>
        )}
      </div>

      <div
        className="shrink-0 flex items-center justify-center gap-2 px-3 py-3"
        style={{ backgroundColor: "rgba(0,0,0,0.88)" }}
      >
        {canEdit && (
          <>
            <button
              type="button"
              onClick={onRemove}
              className="flex-1 max-w-[8.5rem] flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold touch-manipulation active:scale-[0.98] transition-transform"
              style={{
                backgroundColor: "rgba(239,68,68,0.18)",
                color: theme.error,
                border: `1px solid ${theme.error}55`,
              }}
            >
              <Trash2 className="h-4 w-4 shrink-0" />
              <span className="truncate">{labels.removePhoto ?? "Remove"}</span>
            </button>
            <button
              type="button"
              onClick={onRetake}
              className="flex-1 max-w-[8.5rem] flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold touch-manipulation active:scale-[0.98] transition-transform"
              style={{
                backgroundColor: theme.accent,
                color: theme.accentFg,
              }}
            >
              <Camera className="h-4 w-4 shrink-0" />
              <span className="truncate">{labels.retake ?? "Retake"}</span>
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onContinueCapture}
          className="flex-1 max-w-[9.5rem] flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold touch-manipulation active:scale-[0.98] transition-transform"
          style={{
            backgroundColor: "rgba(255,255,255,0.12)",
            color: theme.text,
            border: `1px solid ${theme.border}`,
          }}
        >
          <Plus className="h-4 w-4 shrink-0" />
          <span className="truncate">{labels.nextPhoto ?? "Next photo"}</span>
        </button>
      </div>
    </div>
  );
}
